import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Download, RefreshCw, XCircle, Volume2, Waves, Repeat, Copy, FolderOpen } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { gaEvent } from "../lib/ga";
import { useI18n } from "../lib/i18n";

const STAGES = ["pending", "queued", "analyzing", "generating", "processing", "assembling", "finalizing", "completed"];
const LABEL = {
  en: { pending: "QUEUED", queued: "QUEUED", analyzing: "DIRECTING SCENE", generating: "GENERATING SHOT",
        processing: "PROCESSING", assembling: "ASSEMBLING", finalizing: "FINALIZING",
        completed: "COMPLETED", failed: "FAILED", cancelled: "CANCELLED" },
  fr: { pending: "EN FILE", queued: "EN FILE", analyzing: "MISE EN SCÈNE", generating: "GÉNÉRATION",
        processing: "TRAITEMENT", assembling: "ASSEMBLAGE", finalizing: "FINALISATION",
        completed: "TERMINÉ", failed: "ÉCHEC", cancelled: "ANNULÉ" },
};

const TINT = {
  pending: "#8288a0", queued: "#8288a0", analyzing: "#00d5ff", generating: "#ff4b2b",
  processing: "#8b5cf6", assembling: "#8b5cf6", finalizing: "#ffc76b", completed: "#00ff9d",
};

export function useGenerationPoll(ids, onDone) {
  const [gens, setGens] = useState({});
  useEffect(() => {
    if (!ids.length) return;
    let stop = false;
    const tick = async () => {
      // one batched request for the whole set, not one per id
      const results = await api
        .get(`/generations?ids=${ids.join(",")}`)
        .then((r) => r.data)
        .catch(() => null);
      if (stop || !results) {
        if (!stop) setTimeout(tick, 8000);
        return;
      }
      const map = {};
      results.forEach((g) => { map[g.id] = g; });
      setGens(map);
      const running = ids.some(
        (id) => !map[id] || !["completed", "failed", "cancelled"].includes(map[id].status)
      );
      if (!running) {
        results.forEach((g) =>
          gaEvent(g.status === "completed" ? "generation_complete" : "generation_failed", {
            kind: g.kind, engine: g.model_id_used || g.model_id,
            output_kind: g.output_kind, credits: g.credits_charged,
          })
        );
        onDone?.(results);
        return;
      }
      setTimeout(tick, 5000);
    };
    tick();
    return () => { stop = true; };
  }, [ids.join(","), onDone]); // eslint-disable-line react-hooks/exhaustive-deps
  return gens;
}

export default function GenCard({ gen, onRetry, onReuse, compact = false }) {
  const { locale } = useI18n();
  const [copied, setCopied] = useState(false);
  if (!gen) return null;
  const label = LABEL[locale][gen.status] || gen.status.toUpperCase();
  const idx = STAGES.indexOf(gen.status);
  const pct = gen.status === "completed" ? 100 : Math.max(8, ((idx + 1) / STAGES.length) * 100);
  const done = gen.status === "completed";
  const failed = gen.status === "failed";
  const tint = TINT[gen.status] || "#9194a6";
  const isAudio = gen.kind === "audio" || gen.output_kind === "audio";
  const scanning = ["analyzing", "generating"].includes(gen.status);

  return (
    <div
      data-testid={`gen-card-${gen.id}`}
      className="lift group overflow-hidden rounded-xl border bg-[#0b0b11]"
      style={{ borderColor: done ? "#212230" : `${tint}44` }}
    >
      <div className={`relative aspect-video bg-[#0e0e15] ${scanning ? "scanline" : ""}`}>
        {done && gen.asset_id ? (
          isAudio ? (
            <div className="flash-in grid h-full place-items-center px-6">
              <div className="w-full max-w-[280px] text-center">
                <Waves className="mx-auto h-6 w-6 text-[#ff4b2b]" />
                <audio src={fileUrl(gen.asset_id)} controls className="mt-4 w-full" data-testid="gen-audio" />
              </div>
            </div>
          ) : gen.output_kind === "video" ? (
            <video
              src={fileUrl(gen.asset_id)}
              controls
              playsInline
              className="flash-in h-full w-full object-cover"
              data-testid="gen-video"
            />
          ) : (
            <img src={fileUrl(gen.asset_id)} alt="" className="flash-in h-full w-full object-cover" data-testid="gen-image" />
          )
        ) : (
          <div className="grid h-full place-items-center px-6">
            {failed ? (
              <div className="text-center">
                <XCircle className="mx-auto h-6 w-6 text-[#ff4b4b]" />
                <p className="mono mt-3 text-[10px] uppercase tracking-[0.2em] text-[#ff4b4b]">{label}</p>
                <p className="mt-2 line-clamp-3 text-[11px] text-[#9194a6]">{gen.error}</p>
              </div>
            ) : (
              <div className="w-full max-w-[240px] text-center">
                <p className="mono text-[10px] uppercase tracking-[0.24em]" style={{ color: tint }}>
                  {label}…
                </p>
                <div className="mt-4 h-[3px] w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${pct}%`,
                      background: `linear-gradient(90deg, ${tint}, #ffc76b)`,
                      boxShadow: `0 0 14px ${tint}`,
                    }}
                  />
                </div>
                <div className="mono mt-4 flex justify-center gap-1.5 text-[9px] uppercase tracking-[0.16em] text-[#3d4054]">
                  {STAGES.slice(1, 7).map((s, i) => (
                    <span key={s} style={{ color: i + 1 <= idx ? tint : undefined }}>■</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {done && gen.has_audio && (
          <span className="glass mono absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] uppercase tracking-[0.14em] text-[#7de8ff]">
            <Volume2 className="h-2.5 w-2.5" /> {locale === "fr" ? "audio natif" : "native audio"}
          </span>
        )}
      </div>

      {!compact && (
        <div className="p-4">
          <div className="mono flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.14em] text-[#8288a0]">
            <span style={{ color: tint }}>● {label}</span>
            <span>{gen.kind}</span>
            {gen.params?.duration > 0 && gen.kind === "video" && <span>{gen.params.duration}s</span>}
            <span>{gen.credits_charged} cr</span>
            {gen.model_used && <span className="text-[#7de8ff]">{gen.model_used}</span>}
          </div>
          <p className="mt-3 line-clamp-2 text-[12px] leading-relaxed text-[#9194a6]">
            {gen.params?.raw_prompt || gen.params?.prompt}
          </p>
          {gen.note && (
            <p className="mt-3 flex gap-2 rounded-lg border border-[#ff4b2b]/25 bg-[#ff4b2b]/[0.07] p-2.5 text-[11px] leading-relaxed text-[#f7b98c]">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              {gen.note}
            </p>
          )}
          {done && gen.kind === "video" && gen.output_kind && gen.output_kind !== "video" && (
            <p className="mt-3 flex gap-2 rounded-lg border border-[#ffc76b]/30 bg-[#ffc76b]/[0.07] p-2.5 text-[11px] leading-relaxed text-[#ffc76b]"
               data-testid={`gen-not-video-${gen.id}`}>
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              {locale === "fr"
                ? "Ce rendu est une image clé, pas une vidéo animée : relancez le plan pour obtenir du mouvement."
                : "This render is a key frame, not animated video — relaunch the shot to get real motion."}
            </p>
          )}
          <div className="mono mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[9.5px] uppercase tracking-[0.14em] text-[#5d6176]">
            {gen.params?.resolution && <span>{gen.params.resolution}</span>}
            {gen.params?.quality && <span>{gen.params.quality}</span>}
            {gen.params?.aspect_ratio && <span>{gen.params.aspect_ratio}</span>}
            {gen.model_id && <span>{gen.model_id}</span>}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {onReuse && (gen.params?.raw_prompt || gen.params?.prompt) && (
              <button
                onClick={() => onReuse(gen)}
                data-testid={`gen-reuse-${gen.id}`}
                className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
              >
                <Repeat className="h-3 w-3" /> {locale === "fr" ? "Repartir de cette base" : "Reuse this setup"}
              </button>
            )}
            <button
              onClick={() => {
                navigator.clipboard?.writeText(gen.params?.raw_prompt || gen.params?.prompt || "");
                setCopied(true);
                setTimeout(() => setCopied(false), 1600);
              }}
              data-testid={`gen-copy-${gen.id}`}
              className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
            >
              <Copy className="h-3 w-3" />
              {copied ? (locale === "fr" ? "Copié" : "Copied")
                      : (locale === "fr" ? "Copier le prompt" : "Copy prompt")}
            </button>
            {done && gen.asset_id && (
              <a
                href={fileUrl(gen.asset_id)}
                download
                data-testid="gen-download"
                className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
              >
                <Download className="h-3 w-3" /> {locale === "fr" ? "Télécharger" : "Download"}
              </a>
            )}
            {done && gen.project_id && (
              <Link
                to={`/studio/projects/${gen.project_id}`}
                data-testid={`gen-project-${gen.id}`}
                className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
              >
                <FolderOpen className="h-3 w-3" />
                {locale === "fr" ? "Voir dans le projet" : "Open in project"}
              </Link>
            )}
            {failed && onRetry && (
              <button
                onClick={() => onRetry(gen.id)}
                data-testid="gen-retry"
                className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
              >
                <RefreshCw className="h-3 w-3" /> {locale === "fr" ? "Relancer" : "Retry"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
